import { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Skeleton } from '../../../../ds/components';
import { useIsMobile } from '../../../../ds/hooks';
import { errorMessage } from '../../../../lib/api';
import { queryRoots } from '../../../../lib/events';
import { AvailabilityGrid } from '../../../calendar/AvailabilityGrid';
import { bookingLinkText } from '../../../calendar/EventTypesPage';
import {
  useAvailability,
  useEventTypes,
  useSaveAvailability,
  type Availability,
} from '../../../calendar/api';
import { useAvailabilityForm } from '../../../calendar/availabilityForm';
import { sharedEventType } from '../../../settings/AvailabilitySettingsPage';
import { useAvailabilitySuggestion } from '../api';
import { ActionError, StepFrame, useWizard } from '../StepFrame';
import type { AvailabilitySuggestion } from '../types';
import styles from './AvailabilityStep.module.css';

/** The suggestion as the week of the editor (labels from the stored week). */
function suggestedWeek(stored: Availability, suggestion: AvailabilitySuggestion): Availability {
  return {
    ...stored,
    days: stored.days.map((day) => {
      const proposed = suggestion.days.find((item) => item.weekday === day.weekday);
      return proposed ? { ...day, enabled: proposed.enabled, windows: proposed.windows } : day;
    }),
  };
}

/**
 * Nutzer 07 / M5: the suggestion from the calendar (`GET /setup/user/availability-suggestion`), the week editor of
 * Einstellungen 14 prefilled with it until the week was saved once, buffer/lead time and the booking link of the
 * shared event type. „Vorschlag übernehmen" stores the suggestion, „Weiter" the edited week (`PUT /availability`).
 */
export function AvailabilityStep() {
  const mobile = useIsMobile();
  const wizard = useWizard();
  const queryClient = useQueryClient();
  const availability = useAvailability();
  const suggestion = useAvailabilitySuggestion();
  const eventTypes = useEventTypes();
  const save = useSaveAvailability();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<'suggestion' | 'continue' | null>(null);
  const stepDone = wizard.setup.steps.find((step) => step.key === 'availability')?.status === 'done';

  const base = useMemo(() => {
    if (!availability.data) return undefined;
    if (stepDone || !suggestion.data) return availability.data;
    return suggestedWeek(availability.data, suggestion.data);
  }, [availability.data, suggestion.data, stepDone]);
  const form = useAvailabilityForm(base);
  const shared = sharedEventType(eventTypes.data ?? []);

  async function store(days: Parameters<typeof save.mutateAsync>[0]): Promise<boolean> {
    setError(null);
    try {
      await save.mutateAsync(days);
      await queryClient.invalidateQueries({ queryKey: queryRoots.setup });
      return true;
    } catch (failure) {
      form.setServerError(failure);
      setError(errorMessage(failure));
      return false;
    }
  }

  async function takeSuggestion() {
    if (!suggestion.data || !availability.data) return;
    setBusy('suggestion');
    try {
      await store(
        suggestedWeek(availability.data, suggestion.data).days.map(({ weekday, enabled, windows }) => ({
          weekday,
          enabled,
          windows,
        })),
      );
    } finally {
      setBusy(null);
    }
  }

  async function next() {
    const body = form.prepare();
    if (!body) return;
    setBusy('continue');
    try {
      if (await store(body)) await wizard.act('continue');
    } finally {
      setBusy(null);
    }
  }

  const buffer = shared ? Math.max(shared.bufferBeforeMinutes, shared.bufferAfterMinutes) : null;
  const ready = Boolean(availability.data);

  return (
    <StepFrame
      title="Wann sind Sie für Mandanten erreichbar?"
      text="Wir haben einen Vorschlag aus Ihren Kalenderterminen der letzten vier Wochen abgeleitet. Passen Sie ihn an."
      mobileTitle="Wann sind Sie erreichbar?"
      mobileText="Vorschlag aus Ihren Kalenderterminen der letzten vier Wochen."
      width={860}
      footer={{
        back: wizard.goBack ? { label: 'Zurück', onClick: wizard.goBack } : undefined,
        secondary: {
          label: 'Vorschlag übernehmen',
          onClick: () => void takeSuggestion(),
          loading: busy === 'suggestion',
          disabled: !suggestion.data || !ready,
        },
        primary: {
          label: 'Weiter',
          onClick: () => void next(),
          loading: busy === 'continue' || wizard.acting === 'continue',
          disabled: !ready,
        },
      }}
      mobileFooter={{
        secondary: {
          label: 'Später',
          onClick: () => void wizard.act('skip'),
          loading: wizard.acting === 'skip',
        },
        primary: {
          label: 'Übernehmen',
          onClick: () => void next(),
          loading: busy === 'continue',
          disabled: !ready,
        },
      }}
    >
      {suggestion.data ? (
        <div className={styles.suggestion} role="status">
          {suggestion.data.text}
        </div>
      ) : suggestion.isPending ? (
        <Skeleton variant="block" height={44} />
      ) : null}
      <div className={mobile ? styles.mobileGrid : styles.grid}>
        <div className={mobile ? undefined : styles.weekCard}>
          {ready ? (
            <AvailabilityGrid
              form={form}
              variant={mobile ? 'mobile' : 'settings'}
              disabled={save.isPending}
              onCommit={() => undefined}
            />
          ) : (
            <Skeleton variant="block" height={mobile ? 300 : 380} />
          )}
        </div>
        <div className={mobile ? styles.tilesMobile : styles.side}>
          {mobile ? (
            <>
              <div className={styles.tile}>
                <span className={styles.tileLabel}>Puffer</span>
                <span className={styles.tileValue}>{buffer === null ? '—' : `${buffer} Min`}</span>
              </div>
              <div className={styles.tile}>
                <span className={styles.tileLabel}>Vorlauf</span>
                <span className={styles.tileValue}>{shared ? `${shared.minLeadTimeHours} Std` : '—'}</span>
              </div>
            </>
          ) : (
            <>
              <section className={styles.sideCard} aria-labelledby="buffer-title">
                <h2 id="buffer-title" className={styles.sideTitle}>
                  Puffer und Vorlauf
                </h2>
                <div className={styles.fact}>
                  <span className={styles.factValue}>{buffer === null ? '—' : `${buffer} Min`}</span>
                  <span className={styles.factLabel}>Puffer zwischen Terminen</span>
                </div>
                <div className={styles.fact}>
                  <span className={styles.factValue}>{shared ? `${shared.minLeadTimeHours} Std` : '—'}</span>
                  <span className={styles.factLabel}>Vorlauf bis zur nächsten Buchung</span>
                </div>
              </section>
              <section className={styles.sideCard} aria-labelledby="link-title">
                <h2 id="link-title" className={styles.sideTitle}>
                  Ihr Buchungslink
                </h2>
                <span className={styles.link}>
                  {shared?.bookingUrl ? bookingLinkText(shared.bookingUrl) : 'Kein öffentlicher Termintyp'}
                </span>
                <span className={styles.linkNote}>Wird nach dem Onboarding aktiv.</span>
              </section>
            </>
          )}
        </div>
      </div>
      {error ? (
        <p className={styles.error} role="alert">
          {error}
        </p>
      ) : null}
      <ActionError />
    </StepFrame>
  );
}
